import React, { useState, useEffect } from 'react';
import { Users, Link as LinkIcon, Unlink, Edit, Search, Filter, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from './ui/Button';
import LoadingSpinner from './ui/LoadingSpinner';
import { getUsersWithClientInfo } from '../lib/api';

interface Client {
  id: string;
  name: string;
  company_name: string | null;
}

interface ClientAssociation {
  client_id: string;
  clients: Client;
}

interface UserWithClient {
  user_id: string;
  role: string;
  email: string | null;
  sales_name: string | null;
  client_id: string | null;
  clients: Client | null;
  client_associations?: ClientAssociation[];
}

interface UserManagementListProps {
  onAssignClient: (user: UserWithClient) => void;
  refreshTrigger?: number;
}

const UserManagementList: React.FC<UserManagementListProps> = ({
  onAssignClient,
  refreshTrigger = 0
}) => {
  const [users, setUsers] = useState<UserWithClient[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserWithClient[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'with_client' | 'without_client' | 'admin' | 'client'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getUsersWithClientInfo();
      setUsers(data);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError('Erreur lors du chargement des utilisateurs');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [refreshTrigger]);

  useEffect(() => {
    let filtered = [...users];

    if (searchTerm.trim()) {
      filtered = filtered.filter(user =>
        (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.sales_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.clients?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.clients?.company_name || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    switch (filterType) {
      case 'with_client':
        filtered = filtered.filter(user => user.client_id !== null);
        break;
      case 'without_client':
        filtered = filtered.filter(user => user.client_id === null);
        break;
      case 'admin':
        filtered = filtered.filter(user => user.role === 'admin');
        break;
      case 'client':
        filtered = filtered.filter(user => user.role === 'client');
        break;
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, filterType]);

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'admin':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'client':
        return 'bg-green-100 text-green-700 border-green-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Administrateur';
      case 'client':
        return 'Client';
      default:
        return role;
    }
  };

  const stats = {
    total: users.length,
    withClient: users.filter(u => u.client_id !== null).length,
    withoutClient: users.filter(u => u.client_id === null).length,
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" text="Chargement des utilisateurs..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher par email, nom ou client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl bg-white/80 backdrop-blur-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
          />
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`px-4 py-3 rounded-xl border transition-all duration-200 flex items-center space-x-2 ${
            showFilters
              ? 'bg-purple-500 text-white border-purple-500 shadow-lg'
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm hover:shadow-md'
          }`}
        >
          <Filter className="h-5 w-5" />
          <span>Filtres</span>
        </button>
      </div>

      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden"
          >
            <div className="p-6">
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'all', label: 'Tous les utilisateurs' },
                  { value: 'with_client', label: 'Avec client associé' },
                  { value: 'without_client', label: 'Sans client associé' },
                  { value: 'admin', label: 'Administrateurs' },
                  { value: 'client', label: 'Clients' },
                ].map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => setFilterType(filter.value as any)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      filterType === filter.value
                        ? 'bg-purple-100 text-purple-700 border border-purple-200'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-700 font-medium">Total utilisateurs</p>
              <p className="text-2xl font-bold text-purple-900">{stats.total}</p>
            </div>
            <Users className="h-8 w-8 text-purple-600" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 font-medium">Avec client</p>
              <p className="text-2xl font-bold text-green-900">{stats.withClient}</p>
            </div>
            <LinkIcon className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700 font-medium">Sans client</p>
              <p className="text-2xl font-bold text-gray-900">{stats.withoutClient}</p>
            </div>
            <Unlink className="h-8 w-8 text-gray-600" />
          </div>
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12 bg-white rounded-xl border border-gray-200"
        >
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
            <Users className="h-8 w-8 text-gray-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Aucun utilisateur trouvé
          </h3>
          <p className="text-gray-500">
            {searchTerm
              ? 'Aucun utilisateur ne correspond à vos critères de recherche.'
              : 'Aucun utilisateur enregistré pour le moment.'
            }
          </p>
        </motion.div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rôle
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client associé
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsers.map((user, index) => (
                  <motion.tr
                    key={user.user_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center shadow-sm">
                          <span className="text-white font-semibold text-sm">
                            {(user.email || 'U').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.client_associations && user.client_associations.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {user.client_associations.map((assoc, idx) => (
                            <div key={idx} className="flex items-center">
                              <LinkIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                              <div>
                                <div className="text-sm font-medium text-gray-900">{assoc.clients.name}</div>
                                {assoc.clients.company_name && (
                                  <div className="text-xs text-gray-500">{assoc.clients.company_name}</div>
                                )}
                              </div>
                            </div>
                          ))}
                          {user.client_associations.length > 1 && (
                            <div className="text-xs text-gray-500 mt-1">
                              {user.client_associations.length} clients associés
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center text-gray-400">
                          <Unlink className="h-4 w-4 mr-2" />
                          <span className="text-sm">Non assigné</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button
                        onClick={() => onAssignClient(user)}
                        icon={user.client_associations && user.client_associations.length > 0 ? <Edit className="h-4 w-4" /> : <LinkIcon className="h-4 w-4" />}
                        variant="secondary"
                        className="text-xs"
                      >
                        {user.client_associations && user.client_associations.length > 0 ? 'Gérer' : 'Associer'}
                      </Button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementList;
